import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { formatInTimeZone } from 'date-fns-tz';
import { Dispatch, memo, SetStateAction, useMemo } from 'react';
import {
  BoltCharging,
  ChargerEV,
  CheckMark,
  ErrorWarningtraiangle,
  Info,
  SortingArrowDown,
  SortingArrowUp,
  TableNoData,
} from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { Session } from '../../stores/types/sessions.interface';
import { convertToLocaleCurrency } from '../../utils/Currency.Util';
import { getLocale } from '../../utils/Date.Util';
import { displayEneryKwh } from '../../utils/Number.Util';
import { getConnectorIcon } from '../Charger/utils';
import { ColorType, Icon, Label, LabelType, Pill, Skeleton, Switch } from '../_ui';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { GridPager } from '../_ui/grid/Grid-Pager.component';
import { getChargerStatus, getSessionStatus } from './utils';

interface SessionTableProps {
  data: Session[];
  isLoading: boolean;
  currentPage: number;
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
  onLoadPage: (page: number) => void;
  dataMap?: string[];
  onRowClick: (rowData: any) => void;
  isLocalTimezone: boolean;
  onToggleTimezone: (selected: boolean) => void;
  t: (key: string) => string;
}

const ROW_PER_PAGE = 20;

const getStatusIconAndColor = (session: any): { statusIcon: string; color: ColorType } => {
  let statusIcon;
  let color;
  const status = getChargerStatus(session);
  if (
    ['preparing', 'in_progress', 'finishing', 'suspended_ev', 'suspended_evse'].includes(
      status?.toLowerCase(),
    )
  ) {
    statusIcon = BoltCharging;
    color = ColorType.ACCENT1;
  } else if (session?.status?.toLowerCase() === 'failed') {
    statusIcon = ErrorWarningtraiangle;
    color = ColorType.NEGATIVE1;
  } else {
    statusIcon = CheckMark;
    color = ColorType.POSITIVE1;
  }

  return { statusIcon, color };
};

const useSessionTableColumns = (
  isLoading: boolean,
  isLocalTimezone: boolean,
  onToggleTimezone: (selected: boolean) => void,
  t: (key: string) => string,
  dataMap?: string[],
): ColumnDef<Session>[] => {
  const localTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  return useMemo<ColumnDef<Session>[]>(() => {
    const cols: ColumnDef<Session>[] = [
      {
        id: 'startedVia',
        accessorFn: (row) => t(row.startedVia),
        header: t('session_auth_type'),
        cell: ({ row }) => (
          <Label
            text={t(row.original.startedVia)}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonHeight='h-5'
            skeletonWidth='w-full'
          />
        ),
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'location',
        accessorFn: (row) => row.port?.charger?.location?.name,
        header: t('location'),
        cell: ({ row }) => (
          <Label
            text={row.original?.port?.charger?.location?.name}
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonHeight='h-5'
            skeletonWidth='w-full'
          />
        ),
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'chargerName',
        accessorFn: (row) => row.port?.charger?.name,
        header: t('charger'),
        size: 168,
        minSize: 168,
        cell: ({ row }) => (
          <div className='flex flex-row'>
            <Pill
              label={row.original?.port?.charger?.name}
              bgColor={ColorType.GREY2}
              iconLeft={ChargerEV}
              isLoading={isLoading}
            />
          </div>
        ),
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'startTime',
        accessorFn: (row) => row.startTime || row.createTime,
        header: () => (
          <div className='flex items-center'>
            <span>{t('session_start_time')}</span>
            <Switch
              value={isLocalTimezone}
              onChange={onToggleTimezone}
              labelColor={ColorType.GREY6}
              enableLabel={t('timezone_toggle_local')}
              disableLabel={t('timezone_toggle_local')}
              switchClass='w-5 h-2.5'
              switchBtnClass='w-2 h-2 top-px left-px'
              translateClass='translate-x-2.5'
              className='gap-1 ml-2'
            />
            <Tooltip>
              <TooltipTrigger>
                <Icon
                  src={Info}
                  size={IconSize.SIZE_16x16}
                  color={ColorType.GREY6}
                  className='ml-1'
                />
              </TooltipTrigger>
              <TooltipContent>
                <span className='text-sm text-white font-medium'>
                  {t(isLocalTimezone ? 'local_toggle_message' : 'enable_local_toggle_message')}
                </span>
              </TooltipContent>
            </Tooltip>
          </div>
        ),
        cell: ({ row }) => {
          // Use local timezone if selected by user, otherwise use charger's timezone (falling back to local if not available)
          const timezone = isLocalTimezone
            ? localTimezone
            : row.original.port.charger?.location?.timeZone || localTimezone;
          return (
            <Label
              text={
                isLoading
                  ? ''
                  : formatInTimeZone(
                      row.original?.createTime || row.original?.startTime || '',
                      timezone,
                      'LLL dd, h:mm a',
                      getLocale(),
                    )
              }
              type={LabelType.BODY3}
              color={ColorType.BLACK}
              isLoading={isLoading}
              skeletonHeight='h-5'
              skeletonWidth='w-full'
            />
          );
        },
        sortingFn: 'datetime',
        enableSorting: true,
      },
      {
        id: 'port',
        accessorFn: (row) => t(row.port?.physicalLocation.toLowerCase()),
        header: () => t('port'),
        cell: ({ row }) => {
          if (isLoading) {
            return <Skeleton height='h-5' width='w-full' />;
          }
          return (
            <div className='flex items-center gap-2'>
              <Icon
                key={row.original.port?.connectorType}
                size={IconSize.SIZE_20x20}
                src={getConnectorIcon(row.original?.port?.connectorType)}
                color={ColorType.BLACK}
              />
              <Label
                style={{ width: '80px' }}
                text={t(`${row.original?.port?.physicalLocation.toLowerCase()}`)}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
              />
            </div>
          );
        },
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'status',
        accessorFn: (row) => getSessionStatus(row, t),
        header: t('status'),
        size: 156,
        minSize: 156,
        cell: ({ row }) => {
          if (isLoading) {
            return <Skeleton height='h-5' width='w-full' />;
          }
          const { statusIcon, color } = getStatusIconAndColor(row.original);
          return (
            <div className='flex flex-row gap-2'>
              <Icon src={statusIcon} color={color} size={IconSize.SIZE_20x20} />
              <Label
                text={getSessionStatus(row.original, t)}
                type={LabelType.BODY3}
                color={ColorType.BLACK}
                isLoading={isLoading}
                skeletonHeight='h-5'
                skeletonWidth='w-full'
              />
            </div>
          );
        },
        sortingFn: 'text',
        enableSorting: true,
      },
      {
        id: 'consumedEnergyKwh',
        accessorKey: 'consumedEnergyKwh',
        header: t('overview_energy_used'),
        size: 96,
        minSize: 96,
        cell: ({ row }) => (
          <Label
            text={
              row.original.consumedEnergyKwh || row.original.consumedEnergyKwh === 0
                ? displayEneryKwh(row.original.consumedEnergyKwh, t('kwh'))
                : ''
            }
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonHeight='h-5'
            skeletonWidth='w-full'
          />
        ),
        sortingFn: 'alphanumeric',
        sortUndefined: -1,
        enableSorting: true,
      },
      {
        id: 'billedTotalAmount',
        accessorKey: 'billedTotalAmount',
        header: t('session_cost'),
        size: 96,
        minSize: 96,
        cell: ({ row }) => (
          <Label
            text={
              row.original.billedTotalAmount || row.original.billedTotalAmount === 0
                ? `${convertToLocaleCurrency(
                    row.original.billedTotalAmount,
                    row.original.billedCurrency,
                  )}`
                : ''
            }
            type={LabelType.BODY3}
            color={ColorType.BLACK}
            isLoading={isLoading}
            skeletonHeight='h-5'
            skeletonWidth='w-full'
          />
        ),
        sortingFn: 'alphanumeric',
        sortUndefined: -1,
        enableSorting: true,
      },
    ];
    return cols.filter((col) => !dataMap || (col.id && dataMap.includes(col.id)));
  }, [t, isLoading, isLocalTimezone, localTimezone, dataMap]);
};

export const SessionTable = memo(
  ({
    data,
    isLoading,
    currentPage,
    sorting,
    setSorting,
    onLoadPage,
    dataMap,
    onRowClick,
    isLocalTimezone,
    onToggleTimezone,
    t,
  }: SessionTableProps) => {
    const columns = useSessionTableColumns(
      isLoading,
      isLocalTimezone,
      onToggleTimezone,
      t,
      dataMap,
    );

    const table = useReactTable({
      data: data,
      columns,
      state: {
        sorting,
        pagination: {
          pageIndex: currentPage - 1,
          pageSize: ROW_PER_PAGE,
        },
      },
      autoResetPageIndex: false,
      onSortingChange: setSorting,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getPaginationRowModel: getPaginationRowModel(),
    });

    if (isLoading || data?.length > 0) {
      const totalPages = data?.length > 0 ? Math.ceil(data.length / ROW_PER_PAGE) : 0;

      return (
        <div className='overflow-visible mt-6'>
          <div className='overflow-x-auto overflow-y-visible'>
            <table className='table-auto min-w-full'>
              <thead className='border-t border-b border-grey2'>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className='bg-white'>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        data-column-id={header.column.id}
                        style={{
                          width: header.getSize(),
                          minWidth: header.column.columnDef.minSize ?? 100,
                          position: 'relative',
                        }}
                        className='px-4 py-3 text-left text-sm font-semibold font-inter text-grey6'
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <button
                            type='button'
                            className='absolute right-0 top-1/2 -translate-y-1/2 flex flex-col pr-2'
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            <Icon
                              src={SortingArrowUp}
                              size={IconSize.SIZE_12x12}
                              color={
                                header.column.getIsSorted() === 'asc'
                                  ? ColorType.GREY6
                                  : ColorType.GREY3
                              }
                            />
                            <Icon
                              src={SortingArrowDown}
                              size={IconSize.SIZE_12x12}
                              color={
                                header.column.getIsSorted() === 'desc'
                                  ? ColorType.GREY6
                                  : ColorType.GREY3
                              }
                              className='-mt-1'
                            />
                          </button>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={'hover:bg-grey1 cursor-pointer bg-white'}
                    onClick={() => onRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                        }}
                        className='px-4 py-3 text-sm text-gray-900 overflow-hidden whitespace-nowrap'
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <GridPager
              totalPage={totalPages}
              currentPage={currentPage}
              loadPage={onLoadPage}
              showPageSearch
            />
          )}
        </div>
      );
    }

    return (
      <div className='flex flex-col gap-1 items-center justify-center py-4 h-52'>
        <Icon className='grey5' src={TableNoData} size={IconSize.SIZE_48x48} />
        <Label text={t('recent_session_no_data')} type={LabelType.H4} color={ColorType.GREY5} />
        <Label
          text={t('recent_session_no_data_desc')}
          type={LabelType.BODY3}
          color={ColorType.GREY5}
          className='text-base'
        />
      </div>
    );
  },
);
